/**
 * BookingWizard — public 3-step funnel that warms the lead before sending
 * them to /book.
 *
 * Step 1. Presenting complaint (free-text, chips for common issues).
 * Step 2. Discipline preference (Physio / Osteo / Chiro / Not sure).
 * Step 3. Timeline urgency (This week / 2-4 weeks / Whenever).
 *
 * On submit, we POST analytics to /api/wizard/lead (best-effort), persist the
 * answers to sessionStorage so the booking form / intake form can pre-fill
 * them, then redirect the visitor to /signup?from=wizard (logged-out) or
 * /patient/book (logged-in). Bouncing in 3 quick steps converts undecided
 * visitors who would otherwise drop off on a long booking form.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ChevronRight, ChevronLeft, Stethoscope, CalendarClock, ListChecks, CheckCircle2, UserCircle2 } from 'lucide-react';

const QUICK_COMPLAINTS = [
  'Low back pain', 'Neck pain', 'Shoulder pain', 'Knee pain',
  'Sciatica', 'Headaches / migraines', 'Sports injury', 'Post-surgery rehab',
  'Posture / desk-work issues', 'Pregnancy-related pain',
];

const DISCIPLINES = [
  { k: 'physiotherapy', label: 'Physiotherapy', sub: 'Movement, rehab, manual therapy' },
  { k: 'osteopathy',    label: 'Osteopathy',    sub: 'Whole-body manual treatment' },
  { k: 'chiropractic',  label: 'Chiropractic',  sub: 'Spinal / joint adjustment' },
  { k: 'unsure',        label: "I'm not sure",  sub: "We'll recommend the right service" },
];

const TIMELINES = [
  { k: 'this_week', label: 'This week',      sub: 'Pain is interfering with daily life' },
  { k: 'soon',      label: 'Next 2–4 weeks', sub: 'I want to address it properly' },
  { k: 'flexible',  label: 'Whenever',       sub: 'Just exploring my options' },
];

const STEP_COUNT = 4;

export default function BookingWizard({ onClose, enabledDisciplines }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [complaint, setComplaint] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [timeline, setTimeline] = useState('');
  // Step 4 — optional contact details to pre-fill the signup form.
  // Users CAN skip it (Continue to booking without entering them); the wizard
  // still sends them to /signup where they'll enter the data manually.
  const [yourName, setYourName] = useState('');
  const [yourEmail, setYourEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const visibleDisciplines = DISCIPLINES.filter(d =>
    d.k === 'unsure' || (enabledDisciplines || ['physiotherapy']).includes(d.k)
  );

  // Esc closes the modal.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const canNext = (step === 1 && complaint.trim().length > 2)
               || (step === 2 && discipline)
               || (step === 3 && timeline)
               || (step === 4); // Step 4 is optional — always allow submit.

  const submit = async () => {
    setSubmitting(true);
    const payload = { complaint: complaint.trim(), discipline, timeline,
                       your_name: yourName.trim(), your_email: yourEmail.trim() };
    try {
      sessionStorage.setItem('wizard_answers', JSON.stringify(payload));
      // Best-effort analytics — never block the funnel if it fails.
      api.post('/wizard/lead', payload).catch(() => {});
    } catch (_) { /* ignore */ }
    setSubmitting(false);
    onClose?.();
    // Pre-fill the signup URL params for one-click signup.
    const q = new URLSearchParams({ from: 'wizard', service: discipline });
    if (yourName)  q.set('name',  yourName);
    if (yourEmail) q.set('email', yourEmail);
    navigate(`/signup?${q.toString()}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="booking-wizard">
      <div className="bg-bg-primary rounded-2xl max-w-2xl w-full p-6 md:p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-fg-muted hover:text-fg text-xs uppercase tracking-wider" data-testid="wizard-close">Skip ›</button>

        {/* Progress dots */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i}
                 className={`h-1 flex-1 rounded-full transition ${i <= step ? 'bg-brand' : 'bg-fg-muted/20'}`}/>
          ))}
          <span className="text-xs text-fg-muted ml-2 font-medium">{step}/{STEP_COUNT}</span>
        </div>

        {step === 1 && (
          <div data-testid="wizard-step-1">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-accent font-medium">
              <ListChecks className="w-3.5 h-3.5"/> Step 1 of {STEP_COUNT}
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">What's bringing you in?</h2>
            <p className="text-fg-muted text-sm mb-5">Pick one or describe in your own words.</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="wizard-complaint-chips">
              {QUICK_COMPLAINTS.map(c => (
                <button key={c} type="button" onClick={()=>setComplaint(c)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition ${complaint===c ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
                  {c}
                </button>
              ))}
            </div>
            <textarea
              value={complaint}
              onChange={e=>setComplaint(e.target.value)}
              className="input min-h-[80px]"
              placeholder="Or in your own words — e.g. 'sharp pain in the right lower back after lifting'"
              data-testid="wizard-complaint-input"
            />
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-2">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-accent font-medium">
              <Stethoscope className="w-3.5 h-3.5"/> Step 2 of {STEP_COUNT}
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">Which service feels right?</h2>
            <p className="text-fg-muted text-sm mb-5">If you're not sure, that's fine — we'll match you.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {visibleDisciplines.map(d => (
                <button key={d.k} type="button" onClick={()=>setDiscipline(d.k)}
                        className={`text-left p-4 rounded-lg border transition ${discipline===d.k ? 'border-brand ring-2 ring-brand/20 bg-brand/5' : 'border-soft hover:border-brand/40'}`}
                        data-testid={`wizard-disc-${d.k}`}>
                  <div className="font-medium">{d.label}</div>
                  <div className="text-xs text-fg-muted mt-0.5">{d.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-3">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-accent font-medium">
              <CalendarClock className="w-3.5 h-3.5"/> Step 3 of {STEP_COUNT}
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">How soon do you want to start?</h2>
            <p className="text-fg-muted text-sm mb-5">We'll prioritise your booking accordingly.</p>
            <div className="space-y-2">
              {TIMELINES.map(t => (
                <button key={t.k} type="button" onClick={()=>setTimeline(t.k)}
                        className={`w-full text-left p-4 rounded-lg border transition flex items-center gap-3 ${timeline===t.k ? 'border-brand ring-2 ring-brand/20 bg-brand/5' : 'border-soft hover:border-brand/40'}`}
                        data-testid={`wizard-timeline-${t.k}`}>
                  <div className="flex-1">
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-fg-muted mt-0.5">{t.sub}</div>
                  </div>
                  {timeline === t.k && <CheckCircle2 className="w-5 h-5 text-brand"/>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-4">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-accent font-medium">
              <UserCircle2 className="w-3.5 h-3.5"/> Step 4 of {STEP_COUNT} <span className="text-fg-muted normal-case tracking-normal">(optional)</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">Your details (optional)</h2>
            <p className="text-fg-muted text-sm mb-5">Pre-fill the signup form so we don't ask you twice. Or skip and enter on the next page.</p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-fg-muted font-medium">Your name</span>
                <input className="input mt-1.5" value={yourName} onChange={e=>setYourName(e.target.value)}
                       placeholder="Anna Kowalska" data-testid="wizard-your-name"/>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-fg-muted font-medium">Email</span>
                <input type="email" className="input mt-1.5" value={yourEmail} onChange={e=>setYourEmail(e.target.value)}
                       placeholder="you@example.com" data-testid="wizard-your-email"/>
              </label>
              <p className="text-[11px] text-fg-muted italic">We'll only use this to set up your patient account. No marketing emails.</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-soft">
          {step > 1 ? (
            <button onClick={()=>setStep(s=>s-1)} className="text-sm text-fg-muted inline-flex items-center gap-1 hover:text-fg" data-testid="wizard-back">
              <ChevronLeft className="w-4 h-4"/> Back
            </button>
          ) : <span/>}

          {step < STEP_COUNT ? (
            <button onClick={()=>setStep(s=>s+1)} disabled={!canNext}
                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="wizard-next">
              Next <ChevronRight className="w-4 h-4"/>
            </button>
          ) : (
            <button onClick={submit} disabled={submitting}
                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="wizard-submit">
              {submitting ? 'Saving…' : 'Continue to booking'} <ChevronRight className="w-4 h-4"/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
